import { useEffect, useRef, useMemo, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";
import { AGGClient } from "src/clients";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { Tracking } from "@/analytics/atl-analytics/tracking";
import { useAnalyticsClient } from "@/analytics/wac-analytics/analytics-client";
import { Button } from "@/libraries/design/components";
import { useLocalizedHref } from "@/libraries/web-framework";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";
import { experiments, useExperiment } from "@/utils/experiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { getIsSMBSalesLead } from "./contact-sales-cta-helpers";

const fullWidthStyles = css({
    maxWidth: "none",
    width: "100%"
});

let hasExposureFired = false;

export const useContactSalesCtaEligibility = (): {
    isEligible: boolean;
    isLoading: boolean;
} => {
    const { accountId } = useLoggedInUser();
    // feature gate has configuration to only be enabled on specified page paths
    // https://switcheroo.atlassian.com/ui/gates/c5b6c155-5937-4f7d-9a5f-154015312a23/key/wac_sales_leads_cta_global_nav
    const isFeatureGateEnabled = checkFeatureFlagGate(
        featureFlags.wacSalesLeadsCtaGlobalNav
    );
    const jiraExperiment = useExperiment(
        experiments.gwp10447WacSalesLeadsJiraPT
    );
    const confluenceExperiment = useExperiment(
        experiments.gwp10447WacSalesLeadsConfluencePT
    );

    const jiraExperimentEnrolled = jiraExperiment.type === "enrolled";
    const confluenceExperimentEnrolled =
        confluenceExperiment.type === "enrolled";

    // user is enrolled but not necesarily in the test cohort
    const hasQualifyingGateOrExperiment =
        isFeatureGateEnabled ||
        jiraExperimentEnrolled ||
        confluenceExperimentEnrolled;

    // user is enrolled and served the test variant
    const isInTestCohort =
        isFeatureGateEnabled ||
        (jiraExperimentEnrolled && !!jiraExperiment.value) ||
        (confluenceExperimentEnrolled && !!confluenceExperiment.value);

    const [isSalesLead, setIsSalesLead] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!hasQualifyingGateOrExperiment || !accountId) return;
        setIsLoading(true);
        const aggClient = new AGGClient({
            gatewayUrl:
                process.env["NODE_ENV"] === "production"
                    ? "https://www.atlassian.com/gateway/api/graphql"
                    : "https://wac.stg.internal.atlassian.com/gateway/api/graphql"
        });

        getIsSMBSalesLead({
            accountId,
            aggClient
        })
            .then((response) => {
                setIsSalesLead(!!response);
            })
            .catch(() => {
                console.error(
                    "Error fetching GUPS user profile for SMB Sales lead"
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [accountId, hasQualifyingGateOrExperiment]);

    // Fire a single exposure event once all conditions are met
    useEffect(() => {
        if (!accountId || !isSalesLead) return;
        if (hasExposureFired) return;

        if (jiraExperimentEnrolled) {
            jiraExperiment.fireExposure();
            hasExposureFired = true;
        } else if (confluenceExperimentEnrolled) {
            confluenceExperiment.fireExposure();
            hasExposureFired = true;
        }
    }, [
        accountId,
        isSalesLead,
        jiraExperimentEnrolled,
        confluenceExperimentEnrolled,
        jiraExperiment,
        confluenceExperiment
    ]);

    if (!accountId || !hasQualifyingGateOrExperiment || !isInTestCohort) {
        return { isEligible: false, isLoading: false };
    }

    return {
        isEligible: isSalesLead && isInTestCohort,
        isLoading
    };
};

export const ContactSalesCta = ({ isFullWidth }: { isFullWidth?: boolean }) => {
    const { isEligible, isLoading } = useContactSalesCtaEligibility();
    const productKey = useMemo<ProductKeyTypes>(() => {
        const pathname = globalThis.location?.pathname ?? "";
        if (pathname.includes("/software/jira")) return "jira";
        if (pathname.includes("/software/confluence")) return "confluence";
        if (pathname.includes("/enterprise/contact"))
            return "enterpriseContactSales";
        if (pathname === "/") return "home";
        return undefined;
    }, []);

    if (isLoading || !isEligible) {
        return;
    }

    return (
        <CtaButtonRenderer isFullWidth={isFullWidth} productKey={productKey} />
    );
};

const CtaButtonRenderer = ({
    isFullWidth,
    productKey
}: {
    isFullWidth?: boolean;
    productKey: ProductKeyTypes;
}) => {
    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const localizedHref = useLocalizedHref(
        "/enterprise/contact?formType=product-features"
    );

    const hasFiredRef = useRef(false);

    useEffect(() => {
        if (analyticsClient && !hasFiredRef.current) {
            hasFiredRef.current = true;
            createAnalyticsEvent({
                eventType: "ui",
                action: "viewed",
                actionSubject: "button",
                actionSubjectId: "contactSalesCta",
                source: "globalNav",
                attributes: {
                    isMobile: !!isFullWidth,
                    productKey: productKey
                }
            }).fire();
        }
    }, [analyticsClient, createAnalyticsEvent, isFullWidth]);

    useEffect(() => {
        Tracking.autoTrackLinks(true);
    }, []);

    return (
        <Button
            variant="primary_alt"
            css={isFullWidth && fullWidthStyles}
            href={localizedHref}
            dataEventProps={{
                "data-product-key": productKey,
                "data-event": "clicked",
                "data-event-component": "linkButton",
                "data-event-container": "navbar",
                "data-label-english": "Contact us"
            }}
        >
            <FormattedMessage
                id="global-nav.contact-sales-cta.label"
                defaultMessage="Contact us"
            />
        </Button>
    );
};
